// src/components/settings/DangerZone.js
// Settings "Danger Zone": partial learning-data removal and full account reset.
// Full account deletion is handled manually — the row links to a mailto so the
// owner can cancel billing and purge data with the Admin SDK (see privacy policy).
// Rendered inside a SettingsCard on SettingsPage; confirmations open through the
// global ModalProvider like all other modals.
import { useEffect, useRef, useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { ChatBubbleLeftRightIcon, EnvelopeIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { auth } from '../../firebaseConfig';
import { useAuth } from '../../firebase/useAuth';
import { useModal } from '../../context/ModalContext';
import { useToast } from '../../context/ToastContext';
import { removeLearningData, resetAccountData } from '../../firebase/dangerZone';
import ActionButton from '../common/ActionButton';
import CustomCheckbox from '../common/CustomCheckbox';
import FormField from '../common/FormField';

const DELETION_CONTACT = 'contact@kikikata.app';

const scopeLabel = (scope) =>
  scope.words && scope.sentences
    ? 'all your learned words and sentences'
    : scope.words
      ? 'all your learned words'
      : 'all your learned sentences';

const DangerRow = ({ title, description, children }) => (
  <div className="py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 border-b border-border last:border-b-0">
    <div className="flex flex-col sm:max-w-sm">
      <span className="text-sm font-semibold text-content-primary drop-shadow-sm">{title}</span>
      <p className="mt-1 text-xs text-content-secondary opacity-80 leading-relaxed">{description}</p>
    </div>
    <div className="flex items-center gap-4 flex-wrap sm:justify-end sm:flex-shrink-0">{children}</div>
  </div>
);

// Confirmation dialog shown via ModalProvider. Owns all of its own state so it
// stays live inside the modal stack (the stack stores a static element).
export const DangerZoneConfirmModal = ({ action, scope = {}, style, onMouseDown }) => {
  const { userId } = useAuth();
  const { hideModal } = useModal();
  const { showToast } = useToast();

  const [confirmInput, setConfirmInput] = useState('');
  const [password, setPassword] = useState('');
  const [running, setRunning] = useState(false);

  // Identity gate before destroying data: password users re-enter their
  // password; Google-only users confirm through the account-chooser popup.
  const hasPasswordProvider = auth.currentUser?.providerData?.some((p) => p.providerId === 'password');

  // The modal can be dismissed (Escape/backdrop) while an operation runs; the
  // operation continues, but its completion must not pop whatever modal is now
  // on top of the stack — hideModal() is not targeted at this modal.
  // Set true in the effect body (not just at init) so StrictMode's dev-only
  // mount→unmount→remount cycle doesn't leave the flag stuck at false.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const config = {
    remove: {
      title: 'Remove Learning Data',
      phrase: 'REMOVE',
      body: (
        <>
          This will permanently delete <strong>{scopeLabel(scope)}</strong>, including their SRS progress,
          accuracy stats, and listening time. Your review history and streak are kept. This cannot be undone.
        </>
      ),
      confirmText: 'Remove Data',
    },
    reset: {
      title: 'Reset Account',
      phrase: 'RESET',
      body: (
        <>
          This will permanently delete <strong>all your learning data</strong> — words, sentences, SRS progress,
          review history, streak, and listening stats — returning your account to a fresh state. Your login,
          settings, and subscription are kept. This cannot be undone.
        </>
      ),
      confirmText: 'Reset Account',
    },
  }[action];

  const confirmDisabled = confirmInput !== config.phrase || (hasPasswordProvider && !password);

  const reauthenticate = async () => {
    if (hasPasswordProvider) {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
    } else {
      await reauthenticateWithPopup(auth.currentUser, new GoogleAuthProvider());
    }
  };

  const handleConfirm = async () => {
    setRunning(true);
    try {
      await reauthenticate();
      if (action === 'remove') {
        await removeLearningData(userId, scope);
        showToast('Learning data removed.', 'success');
      } else {
        await resetAccountData(userId);
        showToast('Your account has been reset.', 'success');
      }
      if (mountedRef.current) hideModal();
    } catch (error) {
      console.error(`Danger Zone ${action} failed:`, error);
      if (['auth/wrong-password', 'auth/invalid-credential', 'auth/missing-password'].includes(error.code)) {
        showToast('Incorrect password.', 'error');
      } else if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(error.code)) {
        showToast('Identity confirmation was cancelled.', 'error');
      } else if (error.code === 'auth/too-many-requests') {
        showToast('Too many attempts. Please try again later.', 'error');
      } else {
        showToast('Something went wrong. Please try again.', 'error');
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-md border border-border border-t-4 flex flex-col max-h-[90vh]"
      style={{ borderTopColor: 'var(--color-glassy-red)', ...style }}
      onMouseDown={onMouseDown}
    >
      {/* Header */}
      <div
        className="relative p-4 flex justify-between items-center overflow-hidden flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, var(--color-glassy-red)15, var(--color-glassy-red-to)25)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />
        <div className="relative z-10 flex items-center">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
            style={{
              background: 'var(--color-glassy-red)',
              boxShadow: '0 4px 12px var(--color-glassy-red)30',
            }}
          >
            <ExclamationTriangleIcon className="h-5 w-5 text-white drop-shadow-sm" />
          </div>
          <h3 className="text-lg font-bold text-content-primary">{config.title}</h3>
        </div>
        <button
          onClick={hideModal}
          disabled={running}
          className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors disabled:opacity-40"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <p className="text-sm text-content-secondary leading-relaxed">{config.body}</p>

        <FormField
          id="dangerConfirm"
          label={
            <>
              Type <span className="font-bold text-red-500 select-none">{config.phrase}</span> to confirm
            </>
          }
          type="text"
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value)}
          placeholder={config.phrase}
          autoComplete="off"
        />
        {hasPasswordProvider ? (
          <FormField
            id="dangerPassword"
            label="Your password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        ) : (
          <p className="text-xs text-content-secondary opacity-80">
            You will be asked to confirm your identity with Google before this runs.
          </p>
        )}
      </div>

      {/* Footer */}
      <div
        className="px-6 py-4 flex justify-end space-x-4 border-t border-border flex-shrink-0"
        style={{ background: 'var(--color-background-primary)' }}
      >
        <ActionButton
          variant="glassy"
          color="neutral"
          onClick={hideModal}
          disabled={running}
          className="w-full sm:w-auto px-4 py-2 text-white"
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="glassy"
          color="red"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          loading={running}
          className="w-full sm:w-auto px-4 py-2 text-white"
        >
          {config.confirmText}
        </ActionButton>
      </div>
    </div>
  );
};

function DangerZone() {
  const { email } = useAuth();
  const { showModal } = useModal();
  const [removeScope, setRemoveScope] = useState({ words: false, sentences: false });

  const deletionMailto = `mailto:${DELETION_CONTACT}?subject=${encodeURIComponent('Account Deletion Request')}&body=${encodeURIComponent(
    `Please delete my Kikikata account (${email || 'my email'}).`
  )}`;

  const openSupportChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  return (
    <>
      <DangerRow
        title="Remove Learning Data"
        description="Permanently delete your learned words, sentences, or both. Review history and streak are kept."
      >
        <CustomCheckbox
          checked={removeScope.words}
          onChange={() => setRemoveScope((prev) => ({ ...prev, words: !prev.words }))}
          label="Words"
          color="red"
        />
        <CustomCheckbox
          checked={removeScope.sentences}
          onChange={() => setRemoveScope((prev) => ({ ...prev, sentences: !prev.sentences }))}
          label="Sentences"
          color="red"
        />
        <ActionButton
          variant="glassy"
          color="red"
          className="px-4 py-2 font-semibold"
          disabled={!removeScope.words && !removeScope.sentences}
          onClick={() => showModal(<DangerZoneConfirmModal action="remove" scope={removeScope} />)}
        >
          Remove Data
        </ActionButton>
      </DangerRow>

      <DangerRow
        title="Reset Account"
        description="Wipe all learning data, history, streak, and stats. Keeps your login, settings, and subscription."
      >
        <ActionButton
          variant="glassy"
          color="red"
          className="px-4 py-2 font-semibold"
          onClick={() => showModal(<DangerZoneConfirmModal action="reset" />)}
        >
          Reset Account
        </ActionButton>
      </DangerRow>

      <div className="py-4 sm:py-5">
        <span className="text-sm font-semibold text-content-primary drop-shadow-sm">Delete Account</span>
        <p className="mt-1 text-xs text-content-secondary opacity-80 leading-relaxed">
          Account deletion (including cancelling any active subscription) is handled personally to make sure
          billing is stopped and every trace of your data is removed. Reach out and we&apos;ll take care of it
          promptly — you can:
        </p>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <a
            href={deletionMailto}
            className="flex-1 flex items-center gap-3 p-3 rounded-lg bg-background-secondary/50 border border-border hover:border-primary/50 transition-colors"
          >
            <EnvelopeIcon className="w-5 h-5 text-content-secondary flex-shrink-0" />
            <span className="text-sm text-content-primary">
              Email us at <span className="font-semibold">{DELETION_CONTACT}</span>
            </span>
          </a>
          <button
            type="button"
            onClick={openSupportChat}
            className="flex-1 flex items-center gap-3 p-3 rounded-lg bg-background-secondary/50 border border-border hover:border-primary/50 transition-colors text-left"
          >
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-content-secondary flex-shrink-0" />
            <span className="text-sm text-content-primary">
              Message us in the <span className="font-semibold">live chat</span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}

export default DangerZone;
